// frontend/src/utils/debugConnection.js
// Debug utilities to help diagnose connection and API issues

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5002';

/**
 * Run comprehensive connection diagnostics
 * @returns {Object} Test results and diagnostics
 */
export const debugConnection = async () => {
  const results = {
    timestamp: new Date().toISOString(),
    environment: {
      userAgent: navigator.userAgent,
      url: window.location.href,
      apiUrl: API_URL,
      socketUrl: SOCKET_URL,
      hasToken: !!localStorage.getItem('token')
    },
    tests: []
  };

  // Test 1: Basic API Health Check
  try {
    const healthUrl = API_URL.replace('/api', '/health');
    const response = await fetch(healthUrl, {
      method: 'GET',
      timeout: 5000
    });
    
    if (response.ok) {
      const data = await response.json();
      results.tests.push({
        test: 'API Health Check',
        status: 'success',
        details: data
      });
    } else {
      results.tests.push({
        test: 'API Health Check',
        status: 'failed',
        error: `HTTP ${response.status}: ${response.statusText}`
      });
    }
  } catch (error) {
    results.tests.push({
      test: 'API Health Check',
      status: 'failed',
      error: error.message
    });
  }

  // Test 2: API Authentication Endpoint
  try {
    const response = await fetch(`${API_URL}/auth/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      timeout: 5000
    });
    
    if (response.ok) {
      results.tests.push({
        test: 'API Authentication',
        status: 'success'
      });
    } else {
      results.tests.push({
        test: 'API Authentication',
        status: 'failed',
        error: `HTTP ${response.status}: ${response.statusText}`
      });
    }
  } catch (error) {
    results.tests.push({
      test: 'API Authentication',
      status: 'failed',
      error: error.message
    });
  }

  // Test 3: Socket.io Connection
  try {
    const socketResponse = await fetch(SOCKET_URL, {
      method: 'GET',
      timeout: 5000
    });
    
    results.tests.push({
      test: 'Socket.io Server',
      status: socketResponse.ok ? 'accessible' : 'failed',
      error: socketResponse.ok ? null : `HTTP ${socketResponse.status}`
    });
  } catch (error) {
    results.tests.push({
      test: 'Socket.io Server',
      status: 'failed',
      error: error.message
    });
  }

  // Test 4: CORS Headers
  try {
    const response = await fetch(`${API_URL}/auth/me`, {
      method: 'OPTIONS',
      timeout: 5000
    });
    
    const corsHeaders = {
      'access-control-allow-origin': response.headers.get('access-control-allow-origin'),
      'access-control-allow-methods': response.headers.get('access-control-allow-methods'),
      'access-control-allow-headers': response.headers.get('access-control-allow-headers')
    };
    
    results.tests.push({
      test: 'CORS Configuration',
      status: 'success',
      details: corsHeaders
    });
  } catch (error) {
    results.tests.push({
      test: 'CORS Configuration',
      status: 'failed',
      error: error.message
    });
  }

  // Test 5: Local Storage
  try {
    const testKey = 'debug-test';
    localStorage.setItem(testKey, 'test-value');
    const retrieved = localStorage.getItem(testKey);
    localStorage.removeItem(testKey);
    
    results.tests.push({
      test: 'Local Storage',
      status: retrieved === 'test-value' ? 'success' : 'failed'
    });
  } catch (error) {
    results.tests.push({
      test: 'Local Storage',
      status: 'failed',
      error: error.message
    });
  }

  // Test 6: Network Connectivity
  try {
    const response = await fetch('https://httpbin.org/get', {
      method: 'GET',
      timeout: 5000
    });
    
    results.tests.push({
      test: 'External Network',
      status: response.ok ? 'success' : 'failed'
    });
  } catch (error) {
    results.tests.push({
      test: 'External Network',
      status: 'failed',
      error: error.message
    });
  }

  // Test 7: Ad Blocker Detection
  try {
    const adBlockTest = document.createElement('div');
    adBlockTest.innerHTML = '&nbsp;';
    adBlockTest.className = 'adsbox';
    adBlockTest.style.position = 'absolute';
    adBlockTest.style.left = '-999px';
    document.body.appendChild(adBlockTest);
    
    setTimeout(() => {
      const isBlocked = adBlockTest.offsetHeight === 0;
      results.tests.push({
        test: 'Ad Blocker Detection',
        status: isBlocked ? 'detected' : 'not-detected',
        details: isBlocked ? 'Ad blocker may be interfering with requests' : 'No ad blocker interference detected'
      });
      document.body.removeChild(adBlockTest);
    }, 100);
  } catch (error) {
    results.tests.push({
      test: 'Ad Blocker Detection',
      status: 'failed',
      error: error.message
    });
  }

  return results;
};

/**
 * Generate a readable diagnostic report
 * @param {Object} results - Results from debugConnection()
 * @returns {string} Formatted report
 */
export const generateDiagnosticReport = (results) => {
  let report = `# Connection Diagnostic Report\n`;
  report += `Generated: ${results.timestamp}\n\n`;
  
  report += `## Environment\n`;
  report += `- URL: ${results.environment.url}\n`;
  report += `- API URL: ${results.environment.apiUrl}\n`;
  report += `- Socket URL: ${results.environment.socketUrl}\n`;
  report += `- Has Auth Token: ${results.environment.hasToken}\n`;
  report += `- User Agent: ${results.environment.userAgent}\n\n`;
  
  report += `## Test Results\n`;
  results.tests.forEach(test => {
    const status = test.status === 'success' ? '✅' : 
                  test.status === 'accessible' ? '🟡' : '❌';
    report += `${status} ${test.test}: ${test.status}\n`;
    if (test.error) {
      report += `   Error: ${test.error}\n`;
    }
    if (test.details) {
      report += `   Details: ${JSON.stringify(test.details, null, 2)}\n`;
    }
  });
  
  return report;
};

/**
 * Quick connection test for components
 * @returns {Promise<boolean>} True if basic connectivity works
 */
export const quickConnectionTest = async () => {
  try {
    const healthUrl = API_URL.replace('/api', '/health');
    const response = await fetch(healthUrl, {
      method: 'GET',
      timeout: 3000
    });
    return response.ok;
  } catch (error) {
    console.warn('Quick connection test failed:', error.message);
    return false;
  }
};